import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LoopIcon from '@mui/icons-material/Loop';
import KeyboardReturnOutlinedIcon from '@mui/icons-material/KeyboardReturnOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import WrongLocationIcon from '@mui/icons-material/WrongLocation';
import {
  DataGridPro,
  GridCellParams,
  GridColDef,
  GridRowOrderChangeParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { GridRowIdGetter } from '@mui/x-data-grid/models/gridRows';
import { DateTime } from 'luxon';
import { Link, useNavigate } from 'react-router-dom';
import {
  OnHoldTripLegApproveDenyAction,
  Reservation,
  TripLeg,
  TripLegAddress,
} from '@/data/models/reservation.model';
import { Passenger } from '@/data/models/passenger.model';
import * as reservationSlice from '@/data/reservation.slice';
import { selectJustCreatedReservation } from '@/data/reservation.slice';
import reservationUtils from '@/services/reservationUtils';
import dateUtils from '@/services/DateUtils';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import StatusChip from './StatusChip';
import { TimeZone } from '@/data/models/time-zone.model';
import { useAppSelector } from '@/hooks/useAppSelector';
import * as accountingSlice from '@/data/accounting.slice';
import { mapTripLegTransportationAttributes } from '@/services/transportReservationMapper';
import { scoreAndMergeConfidenceIntoTripLegs } from '@/components/reservations/machines/runBundledValidation';
import { useFeatureAddressValidationForCurrentPortal } from '@/hooks/features/useFeatureAddressValidation';

const PREFIX = 'ReservationAccordion';

const classes = {
  panelTitle: `${PREFIX}-panelTitle`,
  summary: `${PREFIX}-summary`,
  grid: `${PREFIX}-grid`,
  address: `${PREFIX}-address`,
  line2: `${PREFIX}-line2`,
  hasRoundTripIcon: `${PREFIX}-hasRoundTripIcon`,
};

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  [`& .${classes.panelTitle}`]: {
    fontWeight: '600',
    fontSize: 14,
    letterSpacing: 0.17,
    margin: 'auto 0',
  },

  [`& .${classes.summary}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },

  [`& .${classes.address}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.line2}`]: {
    fontWeight: 400,
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },

  [`& .${classes.hasRoundTripIcon}`]: {
    marginLeft: theme.spacing(3),
  },
}));

interface ActionCellProps {
  row: any;
  loadingPassenger: boolean;
  isSavingTrip: boolean;
  isSuperReaderUser: boolean;
  onEdit: (leg: TripLeg) => void;
  onCancel: (leg: TripLeg) => void;
  onActivateWillCall: (leg: TripLeg) => void;
}

const ActionCell = memo(function ActionCell({
  row,
  loadingPassenger,
  isSavingTrip,
  isSuperReaderUser,
  onEdit,
  onCancel,
  onActivateWillCall,
}: ActionCellProps) {
  const t = useCommonTranslation();
  const returnLeg = row.metadata?.roundTripReturnLeg;

  return (
    <>
      {returnLeg && (
        <Tooltip title={t('passenger.roundTrip')}>
          <KeyboardReturnOutlinedIcon />
        </Tooltip>
      )}
      {!isSuperReaderUser && (
        <Tooltip
          title={t('passenger.editTripLeg')}
          className={returnLeg ? '' : classes.hasRoundTripIcon}>
          <span>
            <IconButton
              data-testid="edit"
              disabled={loadingPassenger}
              onClick={(evt) => {
                evt.stopPropagation();
                onEdit(row);
              }}
              size="medium"
              color="primary"
              data-fs-element="EditTripIconButton">
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {!isSuperReaderUser && !row.isRoundTrip && !row.isSingleLegTrip && (
        <Tooltip title={t('passenger.cancelTripLeg')}>
          <span>
            <IconButton
              data-testid="cancel-leg"
              data-fs-element="CancelTripIconButton"
              disabled={
                loadingPassenger || !reservationUtils.isTripLegCancelable(row)
              }
              onClick={(evt) => {
                evt.stopPropagation();
                onCancel(row);
              }}
              color="warning"
              size="medium">
              <WrongLocationIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {!isSuperReaderUser && row.isWillCall && (
        <Tooltip title={t('passenger.activateWillCall')}>
          <span>
            <IconButton
              data-testid="willcall"
              disabled={
                loadingPassenger ||
                isSavingTrip ||
                !reservationUtils.isTripLegActivatable(row)
              }
              onClick={(evt) => {
                evt.stopPropagation();
                onActivateWillCall(row);
              }}
              size="medium"
              color="primary">
              <PhoneIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  );
});

const getTableRowId: GridRowIdGetter<TripLeg> = (row) =>
  row.tripLegReferenceID!;

interface ReservationAccordionProps {
  reservation: Reservation;
  passenger: Passenger | null;
  isSuperReaderUser: boolean;
  loadingPassenger: boolean;
  isSavingTrip: boolean;
  timezones: TimeZone[];
  onEditTripLeg: (leg: TripLeg) => void;
  onCancelTripLeg: (leg: TripLeg) => void;
  onAddTripLeg: (evt: any, resid: string, seq: number) => void;
  onCancelReservation: (
    evt: any,
    id: string,
    standingOrderUUID?: string,
  ) => void;
}

const ReservationAccordion = memo(function ReservationAccordion({
  reservation,
  passenger,
  isSuperReaderUser,
  loadingPassenger,
  isSavingTrip,
  timezones,
  onEditTripLeg,
  onCancelTripLeg,
  onAddTripLeg,
  onCancelReservation,
}: ReservationAccordionProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );
  const addressValidationEnabled =
    useFeatureAddressValidationForCurrentPortal();
  const justCreatedReservation = useAppSelector(selectJustCreatedReservation);

  const [expanded, setExpanded] = useState(false);

  // https://americanlogistics.atlassian.net/browse/F1V2-3923
  // Show a newly created reservation expanded. justCreatedReservation its
  // durable (unlike the transient reservationEventEmitted), so it's still
  // readable even though this accordion typically doesn't mount until after
  // that event fired and was cleared elsewhere.
  useEffect(() => {
    if (!justCreatedReservation?.reservationReferenceID) return;

    if (
      justCreatedReservation?.reservationReferenceID &&
      justCreatedReservation.reservationReferenceID ===
        reservation?.reservationReferenceID
    ) {
      setExpanded(true);
      dispatch(reservationSlice.setJustCreatedReservation(null));
    }
  }, [justCreatedReservation, reservation?.reservationReferenceID, dispatch]);

  const isRoundTrip = reservationUtils.isRoundTrip(reservation);
  const isStandingOrder = !!reservation.standingOrderUUID;
  const referenceID = reservation.reservationReferenceID;
  const firstTripLeg = reservation.tripLegs[0];

  const rows = useMemo(() => {
    const sortedTripLegs = reservationUtils.getSortedTripLegs(
      reservation.tripLegs,
    );

    const isSingleLegTrip = sortedTripLegs.length === 1;

    let roundTripWillCall = false;
    let roundTripReturnTime = '';
    let roundTripReturnTimeZone = '';
    let roundTripTripLegReferenceID = '';

    if (isRoundTrip) {
      roundTripWillCall = sortedTripLegs[1].isWillCall;
      roundTripReturnTime = sortedTripLegs[1].pickUpDueDateTime || '';
      roundTripReturnTimeZone = sortedTripLegs[1].dropOffAddress.timeZone || '';
      roundTripTripLegReferenceID = sortedTripLegs[1].tripLegReferenceID!;
    }

    return sortedTripLegs.map((l) => ({
      ...l,
      isSingleLegTrip,
      roundTripWillCall,
      roundTripReturnTime,
      roundTripReturnTimeZone,
      roundTripTripLegReferenceID,
      reservationReferenceID: reservation.reservationReferenceID,
      reservation,
    }));
  }, [reservation, isRoundTrip]);

  const activateWillCall = useCallback(
    async (leg: TripLeg) => {
      const tz = leg.pickUpAddress.timeZone;
      const now = DateTime.local().plus({ minutes: 1 }).setZone(tz);
      const l = reservationUtils.extractTripLegFields(leg);
      const pickUpDueDateTime = now.toUTC().toISO()!;
      const appointmentDateTime = now.plus({ minutes: 1 }).toUTC().toISO()!;

      const updatedLeg: TripLeg = {
        ...l,
        transportationAttributes: mapTripLegTransportationAttributes(
          // endpoint is returning each attribute in its own array
          leg?.transportationAttributes?.flat(),
          transportationAttributes,
        ),
        pickUpDueDateTime,
        isWillCall: false,
        appointmentDateTime,
      };
      // Mark saving before the pre-dispatch scoring so the action buttons
      // (gated on isSavingTrip) can't fire a second submit during the await.
      dispatch(reservationSlice.setSavingTripLeg(true));
      const legsToSend = addressValidationEnabled
        ? await scoreAndMergeConfidenceIntoTripLegs([updatedLeg])
        : [updatedLeg];
      dispatch(reservationSlice.updateTripLegsAsync(legsToSend, false));
    },
    [dispatch, transportationAttributes, addressValidationEnabled],
  );

  const handleApproveOrDenyLeg = useCallback(
    (action: OnHoldTripLegApproveDenyAction, params: GridCellParams) => {
      const data = reservationUtils.getApproveOrDenyTripLegsFromData(
        params.row.tripLegReferenceID,
        action,
        params.row.reservation,
      );
      dispatch(
        reservationSlice.approveOrDenyTripLegsAsync(
          data.passengerUUID,
          data.reservationReferenceID,
          data.tripLegReferenceIDs,
          data.action,
        ),
      );
    },
    [dispatch],
  );

  const handleRowOrderChange = useCallback(
    (params: GridRowOrderChangeParams) => {
      const legs = reservationUtils.reorderTripLegs(
        reservation.tripLegs,
        params.oldIndex,
        params.targetIndex,
      );
      dispatch(
        reservationSlice.reorderTripLegsAsync(
          passenger!.passengerUUID!,
          reservation.reservationReferenceID!,
          legs,
        ),
      );
    },
    [passenger, dispatch, reservation],
  );

  const handleConvertToMultiLeg = useCallback(async () => {
    const saveAsDraft = reservation.tripLegs[0].state === 'draft';
    const legs = reservation.tripLegs.map((l) => {
      const newLeg = reservationUtils.extractTripLegFields(l);
      return {
        ...newLeg,
        transportationAttributes: mapTripLegTransportationAttributes(
          // endpoint is returning each attribute in its own array
          l?.transportationAttributes?.flat(),
          transportationAttributes,
        ),
        metadata: {
          ...(l.metadata || {}),
          roundTrip: false,
          roundTripReturnLeg: false,
        },
      };
    });
    // Mark saving before the pre-dispatch scoring so the convert button
    // (gated on isSavingTrip) can't fire a second submit during the await.
    dispatch(reservationSlice.setSavingTripLeg(true));
    const legsToSend = addressValidationEnabled
      ? await scoreAndMergeConfidenceIntoTripLegs(legs)
      : legs;
    dispatch(reservationSlice.updateTripLegsAsync(legsToSend, saveAsDraft));
  }, [
    dispatch,
    reservation,
    transportationAttributes,
    addressValidationEnabled,
  ]);

  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      navigate(
        `/passengers/${passenger?.passengerUUID}/reservations/${params.row.reservationReferenceID}?tripLegID=${params.row.tripLegReferenceID}`,
      );
    },
    [navigate, passenger],
  );

  const columns = useMemo<GridColDef[]>(() => {
    const renderAddress = (address: TripLegAddress) => (
      <Box className={classes.address}>
        <Typography>{`${address.addressLine1}`}</Typography>
        <Typography
          className={
            classes.line2
          }>{`${address.city}, ${address.stateCode} ${address.zipCode}`}</Typography>
      </Box>
    );

    return [
      {
        field: 'tripLegReferenceID',
        headerName: t('passenger.tripId'),
        width: 80,
        sortable: false,
      },
      {
        field: 'date',
        headerName: t('passenger.dateTime'),
        width: 180,
        sortable: false,
        renderCell: (params: GridCellParams<TripLeg>): React.ReactElement => {
          const { row } = params;
          if (row.isWillCall) {
            return (
              <Typography textTransform="uppercase">
                {dateUtils.formatAppointmentDate(
                  params.row.appointmentDate,
                  params.row.dropOffAddress.timeZone!,
                ) +
                  ' ' +
                  t('reservation.willCallTime')}
              </Typography>
            );
          }
          return (
            <Typography>
              {dateUtils.formatPickupDateTime(
                params.row.pickUpDueDateTime,
                params.row.pickUpAddress.timeZone!,
              )}
            </Typography>
          );
        },
      },
      {
        field: 'pickupAddress',
        headerName: t('passenger.pickupAddress'),
        flex: 1,
        sortable: false,
        renderCell: (params: GridCellParams<TripLeg>) =>
          renderAddress(params.row.pickUpAddress),
      },
      {
        field: 'dropoffAddress',
        headerName: t('passenger.dropoffAddress'),
        flex: 1,
        sortable: false,
        renderCell: (params: GridCellParams<TripLeg>) =>
          renderAddress(params.row.dropOffAddress),
      },
      {
        field: 'tripStatus',
        headerName: t('passenger.status'),
        headerAlign: 'center',
        width: 160,
        sortable: false,
        align: 'center',
        renderCell: (params: GridCellParams<TripLeg>): React.ReactElement => (
          <StatusChip
            status={params.row.status!}
            onApproveOrDeny={(action: OnHoldTripLegApproveDenyAction) =>
              handleApproveOrDenyLeg(action, params)
            }
          />
        ),
      },
      {
        field: 'actions',
        headerName: ' ',
        width: 150,
        sortable: false,
        renderCell: (params: GridCellParams<TripLeg>) => (
          <ActionCell
            row={params.row}
            loadingPassenger={loadingPassenger}
            isSavingTrip={isSavingTrip}
            isSuperReaderUser={isSuperReaderUser}
            onEdit={onEditTripLeg}
            onCancel={onCancelTripLeg}
            onActivateWillCall={activateWillCall}
          />
        ),
      },
    ];
  }, [
    loadingPassenger,
    isSavingTrip,
    isSuperReaderUser,
    onEditTripLeg,
    onCancelTripLeg,
    activateWillCall,
    handleApproveOrDenyLeg,
  ]);

  let panelTitle: React.ReactNode;
  if (!firstTripLeg?.pickUpAddress) {
    panelTitle = (
      <Typography component="span" className={classes.panelTitle}>
        {`${t('reservation.reservationId')}: `}
        <Link to={`reservations/${referenceID}`}>{referenceID}</Link>
      </Typography>
    );
  } else {
    const timeZone = firstTripLeg.pickUpAddress.timeZone;
    const tz = timezones?.find((z) => z.ianaStandard === timeZone);
    const panelTitleTimeZone = tz?.timeZoneName;
    const reservationTime = dateUtils.formatAppointmentDate(
      firstTripLeg.appointmentDate,
      timeZone!,
    );
    panelTitle = (
      <>
        <Typography component="span" className={classes.panelTitle}>
          {`${t('reservation.reservationId')}: `}
          <Link to={`reservations/${referenceID}`}>{referenceID}</Link>
        </Typography>
        {reservationTime && (
          <Typography
            component="span"
            className={classes.panelTitle}
            textTransform="capitalize">
            {' '}
            - {reservationTime} {panelTitleTimeZone}
          </Typography>
        )}
      </>
    );
  }

  return (
    <StyledAccordion
      expanded={expanded}
      onChange={(_evt, isExpanded) => setExpanded(isExpanded)}
      slotProps={{
        transition: { mountOnEnter: true },
      }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className={classes.summary}>
          <Box className={classes.panelTitle}>{panelTitle}</Box>
          <Box>
            {isStandingOrder && (
              <Tooltip title={t('reservation.standingOrderLabel')}>
                <LoopIcon sx={{ verticalAlign: 'middle', cursor: 'default' }} />
              </Tooltip>
            )}
            {!isSuperReaderUser && isRoundTrip && (
              <Tooltip title={t('passenger.convertToMultiLeg')}>
                <span>
                  <IconButton
                    data-testid="convert-to-multi-leg"
                    disabled={loadingPassenger || isSavingTrip}
                    onClick={(evt) => {
                      evt.stopPropagation();
                      handleConvertToMultiLeg();
                    }}
                    color="primary"
                    size="medium">
                    <KeyboardDoubleArrowRightIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isRoundTrip && !isSuperReaderUser && (
              <Tooltip title={t('passenger.addTripLeg')}>
                <span>
                  <IconButton
                    data-testid="add-leg"
                    disabled={loadingPassenger || isSavingTrip}
                    onClick={(evt) =>
                      onAddTripLeg(
                        evt,
                        reservation.reservationReferenceID!,
                        reservation.tripLegs.length,
                      )
                    }
                    color="primary"
                    size="medium">
                    <PlaceOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip title={t('passenger.cancelReservation')}>
                <span>
                  <IconButton
                    data-testid="cancel-reservation"
                    data-fs-element="CancelReservationIconButton"
                    disabled={
                      loadingPassenger ||
                      isSavingTrip ||
                      !reservationUtils.isReservationCancelable(reservation)
                    }
                    onClick={(evt) =>
                      onCancelReservation(
                        evt,
                        reservation.reservationReferenceID!,
                        reservation.standingOrderUUID,
                      )
                    }
                    color="error"
                    size="medium">
                    <HighlightOffIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <DataGridPro
          getRowId={getTableRowId}
          rows={rows}
          columns={columns}
          // rowReordering={
          //   reservation.tripLegs.length > 1 &&
          //   !isRoundTrip &&
          //   !isSuperReaderUser
          // }
          autoHeight={true}
          rowThreshold={0}
          rowSelection={false}
          className={classes.grid}
          checkboxSelection={false}
          disableColumnMenu={true}
          disableColumnSelector={true}
          disableRowSelectionOnClick={true}
          hideFooter={true}
          onRowClick={handleRowClick}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          onRowOrderChange={handleRowOrderChange}
        />
      </AccordionDetails>
    </StyledAccordion>
  );
});

export default ReservationAccordion;
